import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ObjectState, StatefullValue } from '@meddev/fe-shared';
import { Store } from '@ngxs/store';
import { APP_CONSTANTS, BaseInitAppService, BpiType } from '@shared/core';
import { AuthManagerPwaService, UserInfoModel } from '@shared/data';
import { Dayjs } from '@shared/utils';
import _ from 'lodash';
import {
    catchError,
    combineLatest,
    filter,
    first,
    forkJoin,
    from,
    map,
    mergeMap,
    Observable,
    of,
    switchMap,
    tap,
    throwError,
    toArray,
    withLatestFrom,
} from 'rxjs';
import { BsFreeSlotsRequestDto, ConfirmMcaTimeslotRequestDto } from '../dto';
import {
    BookingAllowanceMapper,
    BookingEligibilityMapper,
    ContractorsBookingMapper,
    LocationsBookingMapper,
    MaskedTimeslotPlaceholdersMapper,
    PrereservationMapper,
    SubcontractorsBookingMapper,
    TimeSlotBookingMapper,
} from '../mapper';
import { BookingMapper } from '../mapper/booking.mapper';
import {
    BookingEligibilityModel,
    BookingFilterInfoModel,
    BookingInfoSelectedFiltersModel,
    BookingPlaceholder,
    MaskedTimeslotPlaceholdersResponse,
} from '../model';
import { BookingDetailsModel } from '../model/booking-allowance.model';
import { TimeSlotBookingModel, TimeSlotMetadata, TimeSlotResultModel } from '../model/timeslot-booking.model';
import { ConfirmationSlotData } from '../store/self-booking/self-booking-state.model';
import { SelfBookingActions } from '../store/self-booking/self-booking.actions';
import { SelfBookingState } from '../store/self-booking/self-booking.state';
import { BOOKING_SERVICE_TOKEN } from '../token';
import { TimeslotRequestCombinationsGenerator } from '../util/request-combinatons-generator';

interface TimeslotRequestCombinations {
    validCombinations: {
        contractorUuid: string;
        locationUuid?: string;
        subcontractorUuid: string;
        serviceUuid: string;
        serviceName: string;
        orgUnitUuids: string[];
    }[];
    bookingInfoValue: BookingDetailsModel;
    generator: TimeslotRequestCombinationsGenerator;
}

@Injectable({
    providedIn: 'root',
})
export class SelfBookingManagerService {
    private selfBookingServiceClientService = inject(BOOKING_SERVICE_TOKEN);
    private store = inject(Store);
    private readonly appConstants = inject(APP_CONSTANTS);
    private readonly baseInitAppService = inject(BaseInitAppService);
    private readonly authManager = inject(AuthManagerPwaService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly bookingAllowanceMapper = inject(BookingAllowanceMapper);
    private readonly locationsBookingMapper = inject(LocationsBookingMapper);
    private readonly contractorsBookingMapper = inject(ContractorsBookingMapper);
    private readonly subcontractorsBookingMapper = inject(SubcontractorsBookingMapper);
    private readonly bookingPlaceholdersMapper = inject(MaskedTimeslotPlaceholdersMapper);
    private readonly bookingEligibilityMapper = inject(BookingEligibilityMapper);
    private readonly bookingMapper = inject(BookingMapper);

    bookingInfoBsServiceName$ = this.store.select(SelfBookingState.bookingInfoBsServiceName$);
    bookingFilterInfo$ = this.store.select(SelfBookingState.bookingFilterInfo$);
    bookingFilterPlaceholders$ = this.store.select(SelfBookingState.bookingFilterPlaceholders$);
    bookingFilters$ = this.createBookingFilters$();
    timeslots$ = this.createTimeslots$();
    loadSlotPreview$ = this.store.select(SelfBookingState.loadSlotPreview$);
    confirmationSlot$ = this.store.select(SelfBookingState.confirmationSlot$);

    constructor() {
        this.initBookingInfoFilterListener();
    }

    initializeBookingFilters(allowanceToken?: string): void {
        if (allowanceToken) {
            this.loadBookingDetails(allowanceToken);
        } else {
            this.loadFilterPlaceholders();
        }
    }

    loadBookingDetails(allowanceToken: string): void {
        const statefullObj = { value: null, state: ObjectState.LOADING };
        this.store
            .dispatch(new SelfBookingActions.SetBookingDetails(statefullObj))
            .pipe(
                switchMap(() => {
                    return this.loadWithAllowanceToken(allowanceToken);
                }),
                catchError(() => {
                    const statefullObj = { value: null, state: ObjectState.ERROR };
                    return this.store.dispatch(new SelfBookingActions.SetBookingDetails(statefullObj));
                }),
                first(),
            )
            .subscribe();
    }

    loadFilterPlaceholders(): void {
        this.selfBookingServiceClientService
            .getBookingPlaceholders()
            .pipe(
                map(placeholders => this.bookingPlaceholdersMapper.mapDtoToModel(placeholders)),
                switchMap(placeholders => {
                    const statefullObj = { value: placeholders, state: ObjectState.VALID };
                    return this.store.dispatch(new SelfBookingActions.SetBookingFilterPlaceholders(statefullObj));
                }),
                first(),
            )
            .subscribe();
    }

    setFilters(selectedFilters: BookingInfoSelectedFiltersModel): void {
        this.store
            .dispatch(
                new SelfBookingActions.SetBookingFilters({
                    contractorUuids: selectedFilters.contractorUuids,
                    locationUuids: selectedFilters.locationUuids,
                    subcontractorsUuids: selectedFilters.subcontractorsUuids,
                    serviceUuids: selectedFilters.serviceUuids,
                }),
            )
            .pipe(
                withLatestFrom(this.store.selectOnce(SelfBookingState.bookingInfo$)),
                switchMap(([, bookingInfo]) => {
                    if (bookingInfo.value?.bookingAllowanceinfo) {
                        return this.getSlotsCombinations().pipe(
                            switchMap(result => {
                                return this.store.dispatch(new SelfBookingActions.SetLoadSlotsPreview(result?.validCombinations.length || 0));
                            }),
                        );
                    }
                    return of(null);
                }),
                first(),
            )
            .subscribe();
    }

    getLoadSlotsPreview(): Observable<number> {
        return this.getSlotsCombinations().pipe(map(result => result.validCombinations.length));
    }

    loadSlots(fromDate: Dayjs, toDate?: Dayjs): void {
        const statefullObj = { value: null, state: ObjectState.LOADING };
        this.store.dispatch(new SelfBookingActions.SetTimeslotResult(statefullObj));

        this.store
            .selectOnce(SelfBookingState.bookingInfo$)
            .pipe(
                switchMap(bookingInfo => {
                    if (bookingInfo.value?.bookingAllowanceinfo) {
                        return this.getSlotsCombinations().pipe(
                            switchMap(result => {
                                return this.handleAllowanceTokenTimeslots(result, fromDate, toDate);
                            }),
                        );
                    } else {
                        return this.handlePlaceholdersTimeslots(fromDate, toDate);
                    }
                }),
                catchError(() => {
                    const statefullObj = { value: null, state: ObjectState.ERROR };
                    return this.store.dispatch(new SelfBookingActions.SetTimeslotResult(statefullObj));
                }),
                first(),
            )
            .subscribe();
    }

    private handleAllowanceTokenTimeslots(combinations: TimeslotRequestCombinations, fromDate: Dayjs, toDate?: Dayjs) {
        const timeslotRequests$ = combinations.generator.generateTimeslotRequests(
            combinations.validCombinations,
            fromDate,
            toDate,
            combinations.bookingInfoValue as BookingDetailsModel,
            this.selfBookingServiceClientService,
        );

        return from(timeslotRequests$).pipe(
            mergeMap(request => request, this.appConstants.maxConcurrentRequests),
            toArray(),
            tap(timeslots => {
                const statefullObj = { value: timeslots, state: ObjectState.VALID };
                this.store.dispatch(new SelfBookingActions.SetTimeslotResult(statefullObj));
            }),
        );
    }

    private handlePlaceholdersTimeslots(fromDate: Dayjs, toDate?: Dayjs) {
        const placeholders$ = this.store.selectOnce(SelfBookingState.bookingFilterPlaceholders$);
        const selectedFilterUuids$ = this.store.selectOnce(SelfBookingState.selectedFilterUuids$);
        const settings$ = this.baseInitAppService.appPwaSettings$.pipe(first());

        return forkJoin([placeholders$, selectedFilterUuids$, settings$]).pipe(
            switchMap(([placeholders, selectedFilterUuids, settings]) => {
                if (placeholders.state !== ObjectState.VALID || !placeholders.value) {
                    throw new Error('Invalid placeholders state');
                }

                if (!settings?.settings?.maskedBsId) {
                    throw new Error('Masked BS ID is not set');
                }

                const bsId = settings?.settings?.maskedBsId;
                const placeholdersValue = placeholders.value;

                const taggedDirectBooking = placeholdersValue.directBooking
                    .filter(c => {
                        const service = placeholdersValue.details.services.find(s => s.maskedId === c.maskedServiceId);
                        if (!service) return false;
                        return service.directBooking === true;
                    })
                    .map(c => ({ ...c, bookingType: 'directBooking' as const }));

                const directBookingKeys = new Set(
                    taggedDirectBooking.map(
                        c => `${c.maskedContractorId}|${c.maskedServiceId}|${c.maskedSubcontractorId}|${c.maskedOrgUnitId || ''}`,
                    ),
                );

                const taggedWaitingQueue = (placeholdersValue.waitingQueue ?? [])
                    .filter(c => {
                        const service = placeholdersValue.details.services.find(s => s.maskedId === c.maskedServiceId);
                        if (!service) return false;
                        if (!service.hasQueue) return false;
                        const key = `${c.maskedContractorId}|${c.maskedServiceId}|${c.maskedSubcontractorId}|${c.maskedOrgUnitId || ''}`;
                        return !directBookingKeys.has(key);
                    })
                    .map(c => ({ ...c, bookingType: 'waitingQueue' as const }));

                let filteredCombinations = [...taggedDirectBooking, ...taggedWaitingQueue];

                if (selectedFilterUuids?.contractorUuids?.length) {
                    filteredCombinations = filteredCombinations.filter(combo =>
                        selectedFilterUuids.contractorUuids!.includes(combo.maskedContractorId),
                    );
                }

                if (selectedFilterUuids?.serviceUuids?.length) {
                    filteredCombinations = filteredCombinations.filter(combo => selectedFilterUuids.serviceUuids!.includes(combo.maskedServiceId));
                }

                if (selectedFilterUuids?.subcontractorsUuids?.length) {
                    filteredCombinations = filteredCombinations.filter(combo =>
                        selectedFilterUuids.subcontractorsUuids!.includes(combo.maskedSubcontractorId),
                    );
                }

                if (selectedFilterUuids?.locationUuids?.length) {
                    filteredCombinations = filteredCombinations.filter(
                        combo => combo.maskedOrgUnitId && selectedFilterUuids.locationUuids!.includes(combo.maskedOrgUnitId),
                    );
                }

                const directBookingCombinations = filteredCombinations.filter(c => c.bookingType === 'directBooking');
                const waitingQueueCombinations = filteredCombinations.filter(c => c.bookingType === 'waitingQueue');

                const waitingQueueResults: TimeSlotResultModel[] = waitingQueueCombinations.map(combination => {
                    const contractor = placeholdersValue.details.contractors.find(c => c.maskedId === combination.maskedContractorId);
                    const service = placeholdersValue.details.services.find(s => s.maskedId === combination.maskedServiceId);
                    const orgUnit = placeholdersValue.details.orgUnits.find(o => o.maskedId === combination.maskedOrgUnitId);
                    const subcontractor = placeholdersValue.details.subcontractors.find(s => s.maskedId === combination.maskedSubcontractorId);

                    const metadata: TimeSlotMetadata = {
                        contractorUuid: combination.maskedContractorId,
                        contractorName: contractor?.name,
                        contractorLocation:
                            contractor?.address && contractor?.postNumber && contractor?.post
                                ? `${contractor.address} ${contractor.postNumber}, ${contractor.post}`
                                : undefined,
                        subcontractorUuid: combination.maskedSubcontractorId,
                        subcontractorName: subcontractor?.name,
                        contractorServiceUuid: combination.maskedServiceId,
                        contractorServiceName: service?.name,
                        locationUuid: combination.maskedOrgUnitId,
                        locationInfo: orgUnit ? `${orgUnit.addressName} ${orgUnit.addressNumber}` : undefined,
                        contractorExternalId: contractor?.maskedExternalId,
                        contractorInfoId: contractor?.maskedCinfoId,
                        subcontractorExternalId: subcontractor?.maskedExternalId,
                        serviceExternalId: service?.maskedExternalId,
                        bpiType: contractor?.bpiType,
                        maskedContractor: contractor,
                        maskedService: service,
                        maskedSubcontractor: subcontractor,
                        bookingType: 'waitingQueue',
                    };

                    return {
                        timeslots: [],
                        metadata,
                    };
                });

                const timeslotRequests$ = this.createTimeslotRequests(directBookingCombinations, placeholdersValue, bsId, fromDate, toDate);
                return from(timeslotRequests$).pipe(
                    mergeMap(
                        request =>
                            request.pipe(
                                catchError(err => {
                                    console.error('Timeslot request failed:', err);
                                    return of(null);
                                }),
                            ),
                        this.appConstants.maxConcurrentRequests,
                    ),
                    toArray(),
                    map(timeslots => [...timeslots.filter(timeslot => timeslot !== null), ...waitingQueueResults]),
                    switchMap(allResults => {
                        if (allResults.length === 0) {
                            return throwError(() => new Error('No timeslots found'));
                        }
                        return of(allResults);
                    }),
                    tap(allResults => {
                        const statefullObj = { value: allResults as TimeSlotResultModel[], state: ObjectState.VALID };
                        this.store.dispatch(new SelfBookingActions.SetTimeslotResult(statefullObj));
                    }),
                );
            }),
        );
    }

    private createTimeslotRequests(
        combinations: (BookingPlaceholder & { bookingType: 'directBooking' | 'waitingQueue' })[],
        placeholders: MaskedTimeslotPlaceholdersResponse,
        bsId: string,
        fromDate: Dayjs,
        toDate?: Dayjs,
    ): Observable<TimeSlotResultModel>[] {
        return combinations.map(combination => {
            const contractor = placeholders.details.contractors.find(c => c.maskedId === combination.maskedContractorId);
            const service = placeholders.details.services.find(s => s.maskedId === combination.maskedServiceId);
            const orgUnit = placeholders.details.orgUnits.find(o => o.maskedId === combination.maskedOrgUnitId);
            const subcontractor = placeholders.details.subcontractors.find(s => s.maskedId === combination.maskedSubcontractorId);

            if (!contractor) {
                throw new Error('Contractor not found');
            }

            const dataDto: BsFreeSlotsRequestDto = {
                contractorUuid: contractor.maskedId,
                subcontractorUuid: subcontractor?.maskedExternalId || '',
                serviceUuid: service?.maskedExternalId || '',
                serviceName: service?.name || '/',
                orgUnitUuids: orgUnit?.externalId || '',
                firstAvailable: false,
                numSlots: this.appConstants.numOfFreeSlotsPerRequest,
                from: fromDate.format(this.appConstants.defaultDateFormat),
                to: toDate ? toDate.format(this.appConstants.defaultDateFormat) : undefined,
            };

            return this.selfBookingServiceClientService.getFreeSlots(bsId, contractor?.maskedId, dataDto).pipe(
                map(responseDto => {
                    const timeslots = responseDto?.map(slotDto => new TimeSlotBookingMapper().mapDtoToModel(slotDto));
                    const subcontractor = placeholders.details.subcontractors.find(s => s.maskedId === combination.maskedSubcontractorId);

                    const metadata: TimeSlotMetadata = {
                        contractorUuid: combination.maskedContractorId,
                        contractorName: contractor?.name,
                        contractorLocation:
                            contractor?.address && contractor?.postNumber && contractor?.post
                                ? `${contractor.address} ${contractor.postNumber}, ${contractor.post}`
                                : undefined,
                        subcontractorUuid: combination.maskedSubcontractorId,
                        subcontractorName: subcontractor?.name,
                        contractorServiceUuid: combination.maskedServiceId,
                        contractorServiceName: service?.name,
                        locationUuid: combination.maskedOrgUnitId,
                        locationInfo: orgUnit ? `${orgUnit.addressName} ${orgUnit.addressNumber}` : undefined,
                        contractorExternalId: contractor?.maskedExternalId,
                        contractorInfoId: contractor?.maskedCinfoId,
                        subcontractorExternalId: subcontractor?.maskedExternalId,
                        serviceExternalId: service?.maskedExternalId,
                        bpiType: contractor?.bpiType,
                        maskedContractor: contractor,
                        maskedService: service,
                        maskedSubcontractor: subcontractor,
                        bookingType: combination.bookingType,
                    };

                    return {
                        timeslots,
                        metadata,
                    };
                }),
            );
        });
    }

    createPrereservation(slotUuid: string): Observable<void> {
        return forkJoin([this.store.selectOnce(SelfBookingState.bookingInfo$), this.store.selectOnce(SelfBookingState.timeslotResult$)]).pipe(
            switchMap(([bookingInfo, timeslotResults]) => {
                const parentResult = timeslotResults.value?.find(result => result.timeslots.some(slot => slot.guid === slotUuid));
                const selectedTimeslot = parentResult?.timeslots.find(slot => slot.guid === slotUuid);
                const timeslotMetadata = parentResult?.metadata as TimeSlotMetadata;

                const mapper = new PrereservationMapper();
                const createPrereservationRequest = mapper.toCreatePrereservationRequestDto(
                    bookingInfo.value as BookingDetailsModel,
                    selectedTimeslot as TimeSlotBookingModel,
                    timeslotMetadata,
                );

                const simplePrereservationDto = mapper.toSimplePrereservationDto('', bookingInfo.value as BookingDetailsModel, timeslotMetadata);

                return this.selfBookingServiceClientService.createPrereservation(createPrereservationRequest).pipe(
                    switchMap(response => {
                        simplePrereservationDto.prereservationId = response.prereservationId;
                        return this.selfBookingServiceClientService.confirmPrereservation(
                            bookingInfo.value?.bookingAllowanceinfo?.allowancetoken as string,
                            simplePrereservationDto,
                        );
                    }),
                    switchMap(() => {
                        return this.store.dispatch(
                            new SelfBookingActions.SetLastBookedAllowanceToken(bookingInfo.value?.bookingAllowanceinfo?.allowancetoken as string),
                        );
                    }),
                );
            }),
            catchError(() => {
                return throwError(() => new Error());
            }),
        );
    }

    getBookingEligibility(serviceExternalId: string): Observable<BookingEligibilityModel> {
        return this.authManager.userInfoData$().pipe(
            filter(userInfo => !!userInfo),
            first(),
            switchMap(userInfo => {
                return this.selfBookingServiceClientService.getBookingEligibility((userInfo as UserInfoModel).id, serviceExternalId);
            }),
            map(dto => this.bookingEligibilityMapper.mapDtoToModel(dto)),
        );
    }

    createBooking(slot: TimeSlotBookingModel, metadata: TimeSlotMetadata) {
        return this.authManager.userInfoData$().pipe(
            filter(userInfo => !!userInfo),
            first(),
            switchMap(userInfo => {
                const bookingRequest: ConfirmMcaTimeslotRequestDto = {
                    bpi: metadata.bpiType!,
                    customerId: (userInfo as UserInfoModel).id,
                    timeslot: slot,
                    maskedContractorInfoId: metadata.contractorInfoId!,
                    // Medifit ids
                    maskedContractorId: metadata.bpiType === BpiType.MEDIFIT ? metadata.maskedContractor?.maskedMedifitId : null,
                    maskedServiceId: metadata.bpiType === BpiType.MEDIFIT ? metadata.maskedService?.maskedMedifitId : null,
                    maskedSubcontractorId: metadata.bpiType === BpiType.MEDIFIT ? metadata.maskedSubcontractor?.maskedMedifitId : null,
                    // external ids
                    maskedContractorExternalId: metadata.bpiType === BpiType.MEDIFIT ? null : metadata.maskedContractor?.maskedExternalId,
                    maskedServiceExternalId: metadata.bpiType === BpiType.MEDIFIT ? null : metadata.maskedService?.maskedExternalId,
                    maskedSubcontractorExternalId: metadata.bpiType === BpiType.MEDIFIT ? null : metadata.maskedSubcontractor?.maskedExternalId,
                };
                return this.selfBookingServiceClientService
                    .createBooking(bookingRequest)
                    .pipe(map(bookingResponse => this.bookingMapper.mapDtoToModel(bookingResponse)));
            }),
        );
    }

    clearData(): void {
        this.store.dispatch(new SelfBookingActions.ResetState());
    }

    setConfirmationSlot(slot: TimeSlotBookingModel, metadata: TimeSlotMetadata): void {
        this.store.dispatch(new SelfBookingActions.SetConfirmationSlot(slot, metadata));
    }

    getConfirmationSlot(): Observable<ConfirmationSlotData | undefined> {
        return this.confirmationSlot$;
    }

    clearConfirmationSlot(): void {
        this.store.dispatch(new SelfBookingActions.ClearConfirmationSlot());
    }

    getAndClearConfirmationSlot(): Observable<ConfirmationSlotData | undefined> {
        return this.confirmationSlot$.pipe(
            first(),
            tap(() => this.clearConfirmationSlot()),
        );
    }

    private initBookingInfoFilterListener(): void {
        combineLatest([this.store.select(SelfBookingState.bookingInfo$), this.store.select(SelfBookingState.selectedFilterUuids$)])
            .pipe(
                tap(([bookingInfo]) => {
                    const bookingFilterInfo: BookingFilterInfoModel = {
                        contractors:
                            bookingInfo.value?.contractors.contractors.map(contractor => ({
                                uuid: contractor.uuid,
                                name: contractor.name || '',
                            })) || [],
                        locations:
                            bookingInfo.value?.locations?.locations.map(location => ({
                                uuid: location.uuid,
                                name: location.address,
                            })) || [],
                        subcontractors:
                            bookingInfo.value?.subcontractors.subcontractors.map(subcontractor => ({
                                uuid: subcontractor.uuid,
                                name: subcontractor.name,
                            })) || [],
                    };
                    const statefulObj = { value: bookingFilterInfo, state: bookingInfo.state };
                    this.store.dispatch(new SelfBookingActions.SetBookingFilterInfo(statefulObj));
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    private getSlotsCombinations(): Observable<TimeslotRequestCombinations> {
        return forkJoin([
            this.store.selectOnce(SelfBookingState.bookingInfo$),
            this.store.selectOnce(SelfBookingState.bookingFilterInfo$),
            this.store.selectOnce(SelfBookingState.selectedFilterUuids$),
        ]).pipe(
            map(([bookingInfo, bookingFilterInfo, selectedFilterUuids]) => {
                if (bookingInfo.state === ObjectState.VALID && bookingInfo.value?.bookingAllowanceinfo) {
                    if (bookingFilterInfo.state !== ObjectState.VALID) {
                        throw new Error('Invalid booking filter info state');
                    }

                    const contractorUuids = selectedFilterUuids?.contractorUuids
                        ? selectedFilterUuids?.contractorUuids
                        : bookingFilterInfo.value?.contractors.map(contractor => contractor.uuid) || [];
                    const locationUuids = selectedFilterUuids?.locationUuids
                        ? selectedFilterUuids?.locationUuids
                        : bookingFilterInfo.value?.locations.map(location => location.uuid) || [];
                    const subcontractorUuids = selectedFilterUuids?.subcontractorsUuids
                        ? selectedFilterUuids?.subcontractorsUuids
                        : bookingFilterInfo.value?.subcontractors.map(subcontractor => subcontractor.uuid) || [];

                    const generator = new TimeslotRequestCombinationsGenerator(this.appConstants);
                    const validCombinations = generator.generateValidCombinations(
                        contractorUuids,
                        subcontractorUuids,
                        locationUuids,
                        bookingInfo.value as BookingDetailsModel,
                    );

                    return { validCombinations, bookingInfoValue: bookingInfo.value, generator };
                }

                throw new Error('Invalid booking state - neither allowance token nor placeholders available');
            }),
        );
    }

    private createTimeslots$(): Observable<StatefullValue<TimeSlotResultModel[] | null, unknown>> {
        return this.store.select(SelfBookingState.timeslotResult$).pipe(
            map(timeslots => {
                if (timeslots.state === ObjectState.VALID) {
                    const clonedTimeslots = _.cloneDeep(timeslots.value);
                    const sortedTimeslots = clonedTimeslots?.sort((a: TimeSlotResultModel, b: TimeSlotResultModel) => {
                        const aSlots = a.timeslots && a.timeslots.length > 0;
                        const bSlots = b.timeslots && b.timeslots.length > 0;
                        return aSlots === bSlots ? 0 : aSlots ? -1 : 1;
                    });
                    const statefullObj = { value: sortedTimeslots || null, state: timeslots.state };
                    return statefullObj;
                }
                return timeslots;
            }),
        );
    }

    private loadWithAllowanceToken(allowanceToken: string): Observable<void> {
        return this.selfBookingServiceClientService.getBookingAllowanceInfo(allowanceToken).pipe(
            switchMap(bookingInfo => {
                return forkJoin({
                    contractors: this.selfBookingServiceClientService.getContractors(bookingInfo.contractorUuids),
                    locations: this.selfBookingServiceClientService.getLocations(bookingInfo.contractorUuids),
                    subcontractorsAndServices: this.selfBookingServiceClientService
                        .getSubcontractors(
                            bookingInfo.contractorUuids,
                            bookingInfo.bsId,
                            bookingInfo.service.bsServices?.[0].id as string, // TODO - can there be multiple?
                        )
                        .pipe(
                            switchMap(subcontractors => {
                                const serviceUuids = _.uniq(subcontractors.subcontractors.map(subcontractor => subcontractor.serviceUuids).flat());
                                return this.selfBookingServiceClientService.getServices(serviceUuids).pipe(
                                    map(services => ({
                                        subcontractors,
                                        services,
                                    })),
                                );
                            }),
                        ),
                }).pipe(
                    switchMap(({ contractors, locations, subcontractorsAndServices }) => {
                        const { subcontractors, services } = subcontractorsAndServices;

                        const bookingDetailsModel: BookingDetailsModel = {
                            bookingAllowanceinfo: this.bookingAllowanceMapper.mapDtoToModel(bookingInfo),
                            locations: this.locationsBookingMapper.mapDtoToModel(locations),
                            contractors: this.contractorsBookingMapper.mapDtoToModel(contractors),
                            subcontractors: this.subcontractorsBookingMapper.mapDtoToModel(subcontractors, services),
                        };
                        const statefullObj = { value: bookingDetailsModel, state: ObjectState.VALID };
                        return this.store.dispatch(new SelfBookingActions.SetBookingDetails(statefullObj));
                    }),
                );
            }),
        );
    }

    private createBookingFilters$(): Observable<StatefullValue<BookingFilterInfoModel | null, unknown>> {
        return combineLatest([
            this.store.select(SelfBookingState.bookingInfo$),
            this.store.select(SelfBookingState.bookingFilterPlaceholders$),
            this.bookingInfoBsServiceName$,
        ]).pipe(
            map(([bookingInfo, placeholders, serviceName]) => {
                if (bookingInfo.state === ObjectState.VALID && bookingInfo.value?.bookingAllowanceinfo) {
                    return {
                        state: bookingInfo.state,
                        value: {
                            contractors: bookingInfo.value.contractors.contractors.map(c => ({
                                uuid: c.uuid,
                                name: c.name || '',
                            })),
                            locations:
                                bookingInfo.value.locations?.locations.map(l => ({
                                    uuid: l.uuid,
                                    name: l.address,
                                })) ?? [],
                            subcontractors: bookingInfo.value.subcontractors.subcontractors.map(s => ({
                                uuid: s.uuid,
                                name: s.name,
                            })),
                            services: [{ uuid: 'fixed', name: serviceName.value || '' }],
                            isFixedService: true,
                        },
                    };
                }

                if (placeholders.state === ObjectState.VALID && placeholders.value) {
                    return {
                        state: placeholders.state,
                        value: {
                            contractors: placeholders.value.details.contractors.map(c => ({
                                uuid: c.maskedId,
                                name: c.name,
                            })),
                            locations: placeholders.value.details.orgUnits.map(o => ({
                                uuid: o.maskedId,
                                name: o.name,
                            })),
                            subcontractors: placeholders.value.details.subcontractors.map(s => ({
                                uuid: s.maskedId,
                                name: s.name,
                            })),
                            services: placeholders.value.details.services.map(s => ({
                                uuid: s.maskedId,
                                name: s.name,
                            })),
                            isFixedService: false,
                        },
                    };
                }

                return { state: ObjectState.LOADING, value: null };
            }),
        );
    }

    getFilteredOptions(currentSelections: BookingInfoSelectedFiltersModel): Observable<BookingFilterInfoModel | null> {
        return this.store.select(SelfBookingState.bookingFilterPlaceholders$).pipe(
            first(),
            map(placeholders => {
                if (placeholders.state !== ObjectState.VALID || !placeholders.value) {
                    return null;
                }

                const placeholdersValue = placeholders.value;

                const taggedDirectBooking = placeholdersValue.directBooking
                    .filter(c => {
                        const service = placeholdersValue.details.services.find(s => s.maskedId === c.maskedServiceId);
                        if (!service) return false;
                        return service.directBooking === true;
                    })
                    .map(c => ({ ...c, bookingType: 'directBooking' as const }));

                const directBookingKeys = new Set(
                    taggedDirectBooking.map(
                        c => `${c.maskedContractorId}|${c.maskedServiceId}|${c.maskedSubcontractorId}|${c.maskedOrgUnitId || ''}`,
                    ),
                );

                const taggedWaitingQueue = (placeholdersValue.waitingQueue ?? [])
                    .filter(c => {
                        const service = placeholdersValue.details.services.find(s => s.maskedId === c.maskedServiceId);
                        if (!service) return false;
                        if (!service.hasQueue) return false;
                        const key = `${c.maskedContractorId}|${c.maskedServiceId}|${c.maskedSubcontractorId}|${c.maskedOrgUnitId || ''}`;
                        return !directBookingKeys.has(key);
                    })
                    .map(c => ({ ...c, bookingType: 'waitingQueue' as const }));

                let filteredCombinations = [...taggedDirectBooking, ...taggedWaitingQueue];

                if (currentSelections.contractorUuids?.length) {
                    filteredCombinations = filteredCombinations.filter(combo =>
                        currentSelections.contractorUuids!.includes(combo.maskedContractorId),
                    );
                }

                if (currentSelections.serviceUuids?.length) {
                    filteredCombinations = filteredCombinations.filter(combo => currentSelections.serviceUuids!.includes(combo.maskedServiceId));
                }

                if (currentSelections.subcontractorsUuids?.length) {
                    filteredCombinations = filteredCombinations.filter(combo =>
                        currentSelections.subcontractorsUuids!.includes(combo.maskedSubcontractorId),
                    );
                }

                if (currentSelections.locationUuids?.length) {
                    filteredCombinations = filteredCombinations.filter(
                        combo => combo.maskedOrgUnitId && currentSelections.locationUuids!.includes(combo.maskedOrgUnitId),
                    );
                }

                const validContractorIds = [...new Set(filteredCombinations.map(c => c.maskedContractorId))];
                const validServiceIds = [...new Set(filteredCombinations.map(c => c.maskedServiceId))];
                const validSubcontractorIds = [...new Set(filteredCombinations.map(c => c.maskedSubcontractorId))];
                const validOrgUnitIds = [...new Set(filteredCombinations.map(c => c.maskedOrgUnitId).filter(Boolean))];

                const filteredContractors = placeholdersValue.details.contractors.filter(c => validContractorIds.includes(c.maskedId));

                const filteredServices = placeholdersValue.details.services.filter(s => validServiceIds.includes(s.maskedId));

                const filteredSubcontractors = placeholdersValue.details.subcontractors.filter(s => validSubcontractorIds.includes(s.maskedId));

                const filteredOrgUnits = placeholdersValue.details.orgUnits.filter(o => validOrgUnitIds.includes(o.maskedId));

                return {
                    contractors: filteredContractors.map(c => ({
                        uuid: c.maskedId,
                        name: c.name,
                    })),
                    locations: filteredOrgUnits.map(o => ({
                        uuid: o.maskedId,
                        name: o.name,
                    })),
                    subcontractors: filteredSubcontractors.map(s => ({
                        uuid: s.maskedId,
                        name: s.name,
                    })),
                    services: filteredServices.map(s => ({
                        uuid: s.maskedId,
                        name: s.name,
                    })),
                    isFixedService: false,
                };
            }),
        );
    }
}
